import * as THREE from 'three';
import { ZoneState, ZONE_NAMES, ZONE_SIZE } from '@shared/protocol';

/** How far (in blocks) a sign can be activated from. */
const SIGN_REACH = 6;
/** Confirmation window for freeing an owned zone. */
const CONFIRM_MS = 4000;

/**
 * Four zone signs placed around the world center, one per quadrant.
 * Clicking a free sign claims that zone; clicking your own sign twice
 * (confirmation) frees it and destroys all its blocks.
 */
export class Signs {
  readonly group = new THREE.Group();

  private readonly boards: THREE.Mesh[] = [];
  private readonly textures: THREE.CanvasTexture[] = [];
  private readonly canvases: HTMLCanvasElement[] = [];
  private readonly raycaster = new THREE.Raycaster();
  private zones: ZoneState[] = [];
  private selfId = '';
  private pendingFree: { zone: number; at: number } | null = null;

  constructor(
    private readonly actions: {
      claim(zone: number): void;
      free(zone: number): void;
      toast(text: string): void;
    },
  ) {
    const postMaterial = new THREE.MeshLambertMaterial({ color: 0x6b4a2f });
    // Signs sit diagonally out from the center, each inside its own zone,
    // facing the center point.
    for (let zone = 0; zone < 4; zone++) {
      const inEast = (zone & 1) !== 0; // zone x >= 256
      const inSouth = (zone & 2) !== 0; // zone z >= 256
      const x = ZONE_SIZE + (inEast ? 4.5 : -4.5);
      const z = ZONE_SIZE + (inSouth ? 4.5 : -4.5);

      const holder = new THREE.Group();
      holder.position.set(x, 1, z);
      holder.lookAt(ZONE_SIZE, 1, ZONE_SIZE);

      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 1.4, 0.12),
        postMaterial,
      );
      post.position.y = 0.7;
      post.castShadow = true;

      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 128;
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;

      const board = new THREE.Mesh(
        new THREE.BoxGeometry(2.2, 1.1, 0.08),
        new THREE.MeshLambertMaterial({ map: texture }),
      );
      board.position.y = 1.95;
      board.castShadow = true;
      board.userData.zone = zone;

      holder.add(post, board);
      this.group.add(holder);
      this.boards.push(board);
      this.textures.push(texture);
      this.canvases.push(canvas);
    }
  }

  setState(zones: ZoneState[], selfId: string): void {
    this.zones = zones;
    this.selfId = selfId;
    for (let i = 0; i < 4; i++) this.redraw(i);
  }

  updateZone(zone: ZoneState): void {
    this.zones[zone.index] = zone;
    this.redraw(zone.index);
  }

  /** Returns true if the click was aimed at a sign (and handled). */
  tryClick(camera: THREE.PerspectiveCamera, button: number): boolean {
    if (button !== 0) return false;
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    this.raycaster.far = SIGN_REACH;
    const hits = this.raycaster.intersectObjects(this.boards, false);
    if (hits.length === 0) return false;

    const zoneIndex = hits[0].object.userData.zone as number;
    const zone = this.zones[zoneIndex];
    if (!zone) return true;

    if (zone.ownerId === null) {
      this.actions.claim(zoneIndex);
    } else if (zone.ownerId === this.selfId) {
      if (
        this.pendingFree?.zone === zoneIndex &&
        performance.now() - this.pendingFree.at < CONFIRM_MS
      ) {
        this.pendingFree = null;
        this.actions.free(zoneIndex);
      } else {
        this.pendingFree = { zone: zoneIndex, at: performance.now() };
        this.actions.toast(
          'Click the sign again to FREE this zone - all its blocks will be destroyed!',
        );
      }
    } else {
      this.actions.toast(`This zone belongs to ${zone.ownerNick ?? 'someone else'}`);
    }
    return true;
  }

  private redraw(index: number): void {
    const zone = this.zones[index];
    const canvas = this.canvases[index];
    const ctx = canvas.getContext('2d')!;

    const own = zone?.ownerId === this.selfId && this.selfId !== '';
    const free = !zone || zone.ownerId === null;

    ctx.fillStyle = own ? '#2e5d3a' : free ? '#8a6a45' : '#5d3a3a';
    ctx.fillRect(0, 0, 256, 128);
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 4;
    ctx.strokeRect(4, 4, 248, 120);

    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.font = 'bold 26px system-ui, sans-serif';
    ctx.fillText(`${ZONE_NAMES[index]} zone`, 128, 42);
    ctx.font = '22px system-ui, sans-serif';
    if (free) {
      ctx.fillText('FREE', 128, 78);
      ctx.font = '17px system-ui, sans-serif';
      ctx.fillText('click to claim', 128, 108);
    } else if (own) {
      ctx.fillText('YOURS', 128, 78);
      ctx.font = '17px system-ui, sans-serif';
      ctx.fillText('click twice to free', 128, 108);
    } else {
      ctx.fillText(zone.ownerNick ?? '?', 128, 82);
    }
    this.textures[index].needsUpdate = true;
  }
}
